// frontend/src/components/chat/ChatWindow.jsx

import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { X, Send, Trash2 } from "lucide-react";
import styles from "../../styles/ChatWindow.module.css";
import { useAuth } from "../../lib/auth";
import { useNavigate } from "react-router-dom";
import apiClient from "../../Api/apiConfig";
import { FEATURES } from "../../config/features";

const STORAGE_KEYS = {
  nono: "chatHistory_nono",
  santa: "chatHistory_santa",
};

const API_ORIGIN =
  import.meta.env.VITE_API_ORIGIN ||
  (import.meta.env.VITE_API_URL?.startsWith("http")
    ? new URL(import.meta.env.VITE_API_URL).origin
    : window.location.origin);

const NOURREDINE_AVATAR = "/uploads/1778842173296-nono25.png";

function resolveMediaUrl(url) {
  if (!url) return "";
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:")
  ) {
    return url;
  }

  const cleanUrl = url.startsWith("/") ? url : `/${url}`;
  return `${API_ORIGIN}${cleanUrl}`;
}

function getUserAvatar(user) {
  return (
    user?.avatar_url ||
    user?.avatar ||
    user?.profile_image ||
    user?.photo ||
    NOURREDINE_AVATAR
  );
}

const ChatWindow = ({ onClose }) => {
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [mode, setMode] = useState("nono");

  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const chatBodyRef = useRef(null);

  const avatarSrc = resolveMediaUrl(getUserAvatar(user));

  useEffect(() => {
    if (!isAuthenticated || (user?.role !== "admin" && user?.role !== "member")) {
      alert("Accès réservé aux adhérents et administrateurs.");
      navigate("/login");
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    const key = STORAGE_KEYS[mode];
    const saved = localStorage.getItem(key);

    if (saved) {
      try {
        setChatHistory(JSON.parse(saved));
      } catch (err) {
        console.error("Erreur lors de la récupération de l'historique :", err);
        setChatHistory([]);
      }
    } else {
      setChatHistory([]);
    }
  }, [mode]);

  useEffect(() => {
    const key = STORAGE_KEYS[mode];
    localStorage.setItem(key, JSON.stringify(chatHistory));
  }, [chatHistory, mode]);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [chatHistory, loading]);

  const handleSend = async () => {
    if (message.trim() === "") {
      setError("Le message ne peut pas être vide.");
      return;
    }

    setError("");

    const currentMessage = message.trim();
    setChatHistory((prev) => [...prev, { sender: "user", content: currentMessage }]);
    setMessage("");
    setLoading(true);

    try {
      let aiText = "";

      if (mode === "nono") {
        const response = await apiClient.post("/gemini/generate-letter", {
          prompt: currentMessage,
        });

        aiText =
          response?.data?.letter ||
          response?.data?.message ||
          response?.data?.reply ||
          "Je n'ai pas pu générer la réponse attendue.";
      } else if (FEATURES.santaExperience && mode === "santa") {
        const response = await apiClient.post("/santa/chat", {
          sender_id: user?.id || 0,
          message: currentMessage,
        });

        aiText =
          response?.data?.reply ||
          "Ho ho ho ! Père Noël n'a pas entendu, peux-tu répéter mon enfant ?";
      }

      setChatHistory((prev) => [
        ...prev,
        {
          sender: "ai",
          content:
            aiText || "Désolé, je n'ai pas pu répondre à votre demande.",
        },
      ]);
    } catch (err) {
      console.error("Erreur :", err);

      setChatHistory((prev) => [
        ...prev,
        {
          sender: "ai",
          content:
            FEATURES.santaExperience && mode === "santa"
              ? "Ho ho ho... Une erreur chez les lutins. Réessaie dans un instant."
              : "Désolé, je n'ai pas pu répondre à votre demande.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey && !loading) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = () => {
    if (
      window.confirm(
        `Effacer toutes les conversations avec ${
          FEATURES.santaExperience && mode === "santa" ? "le Père Noël" : "Nono"
        } ?`
      )
    ) {
      setChatHistory([]);
      localStorage.removeItem(STORAGE_KEYS[mode]);
    }
  };

  const title =
    FEATURES.santaExperience && mode === "santa"
      ? "Chat avec le Père Noël 🎅"
      : "Chat avec l'IA de Nono";

  const tooltipText =
    FEATURES.santaExperience && mode === "santa"
      ? "Je suis le vrai Père Noël, version IA 🎅"
      : "Assistant IA de Nourredine Debbari";

  return (
    <div className={styles.window}>
      <div className={styles.header}>
        <div
          className={styles.headerContent}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <img
            src={avatarSrc}
            alt="Nourredine Debbari"
            className={styles.robotIcon}
          />

          {showTooltip && <div className={styles.tooltip}>{tooltipText}</div>}

          <h3 className={styles.title}>{title}</h3>
        </div>

        <div className={styles.modeSwitch}>
          {/* <button
            className={mode === "nono" ? styles.modeButtonActive : styles.modeButton}
            onClick={() => setMode("nono")}
            type="button"
          >
            
          </button> */}

          {FEATURES.santaExperience && (
            <button
              className={
                mode === "santa" ? styles.modeButtonActive : styles.modeButton
              }
              onClick={() => setMode("santa")}
              type="button"
            >
              Père Noël
            </button>
          )}
        </div>

        <div>
          <button
            onClick={handleClearChat}
            className={styles.clearButton}
            aria-label="Effacer les conversations"
            type="button"
          >
            <Trash2 className={styles.icon} />
          </button>

          <button
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Fermer le chat"
            type="button"
          >
            <X className={styles.icon} />
          </button>
        </div>
      </div>

      <div className={styles.body} ref={chatBodyRef}>
        {chatHistory.map((entry, index) => (
          <div
            key={`${entry.sender}-${index}`}
            className={
              entry.sender === "user" ? styles.userMessage : styles.aiMessage
            }
          >
            <div className={styles.messageBubble}>{entry.content}</div>
          </div>
        ))}

        {loading && (
          <p className={styles.loading}>
            {FEATURES.santaExperience && mode === "santa"
              ? "Le Père Noël prépare sa réponse..."
              : "Nono est en train de répondre..."}
          </p>
        )}
      </div>

      <div className={styles.footer}>
        {error && <p className={styles.error}>{error}</p>}

        <input
          type="text"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={handleKeyDown}
          className={styles.input}
          placeholder={
            FEATURES.santaExperience && mode === "santa"
              ? "Écris ton message au Père Noël..."
              : "Écrivez votre message..."
          }
          disabled={loading}
        />

        <button
          className={styles.sendButton}
          onClick={handleSend}
          aria-label="Envoyer le message"
          disabled={loading}
          type="button"
        >
          <Send className={styles.sendIcon} />
        </button>
      </div>
    </div>
  );
};

ChatWindow.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default ChatWindow;